"use client"

import { useState, useEffect } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useRouter, usePathname } from "next/navigation"
import dynamic from "next/dynamic"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { track } from "@/lib/analytics"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { SearchTrigger } from "@/components/search/search-trigger"
import { UserCircleIcon } from "@/components/ui/icons"
import { CountryGate } from "@/components/layout/country-gate"
import {
  DropdownMenu,
  HowItWorksMegaMenu,
  WhatWeOfferMegaMenu,
} from "./mega-menu"
import { MegaMenuPreloader } from "./mega-menu-preloader"
import { buildLocaleSwitchUrl } from "@/components/ui/navbar/locale-picker"
// Imported STATICALLY, and it has to stay that way (AHD-9096).
//
// This was a `dynamic()` with neither `ssr: false` nor `loading`, which is the
// one combination `next/dynamic` gives NO boundary of its own —
// `hasSuspenseBoundary = !opts.ssr || !!opts.loading` in next's
// `shared/lib/lazy-dynamic/loadable.js`, and `ssr` defaults to `true`, so `Wrap`
// is a Fragment. Its `import()` therefore suspended during SSR right here in
// the navbar and escaped to whatever boundary sat above — which was the locale
// layout's `<Suspense fallback={null}>` wrapping the whole page.
//
// Removing those boundaries (AHD-9096) is what actually fixed the blank first
// paint, and it also makes this line harmless in isolation — measured, swapping
// it alone changed nothing. It is still worth being a static import: the
// `dynamic()` saved no bytes, since `./locale-picker` is already imported
// statically in this file (`buildLocaleSwitchUrl`, above) and in
// `page-shell.tsx` (`FooterLocalePicker`), and an unbounded lazy in the navbar
// is a landmine for anyone who adds a boundary above it later. A static import
// also keeps the picker server-rendered, which `loading: () => null` would not.
//
// `e2e/static-shell.spec.ts` (under the `Playwright` check) is what would catch
// a reintroduction — not `scripts/validation/edge-cache.ts`, which is a manual
// diagnostic wired into no check.
import { DesktopLocalePicker } from "./locale-picker"
import { type Country } from "@/i18n/config"
import type { NavbarProps } from "./types"

const MobileMenu = dynamic(
  () => import("./mobile-menu").then((m) => ({ default: m.MobileMenu })),
  { ssr: false, loading: () => null }
)

// Kick the MobileMenu chunk on the first hint that the user intends to open
// it. The fetch then races with the tap-to-release / focus-to-click window so
// the chunk is usually already parsed by the time `mobileOpen` flips true.
// Without this, the chunk only starts loading on click — which on cold
// cache + 4G lands a visible beat after the tap.
function preloadMobileMenu() {
  if (typeof window !== "undefined") void import("./mobile-menu")
}

export type { NavbarItem, NavbarProps } from "./types"

export function Navbar({
  logo,
  items,
  ctaLabel,
  ctaHref,
  onCtaClick,
  className,
  transparent = false,
  navHiddenPaths = [],
  bannerVisible = true,
}: NavbarProps) {
  // Desktop nav hides items flagged `hideOnDesktop` (Pricing + Contact us on
  // CH — see useLocalizedNavigation).
  const desktopItems = items.filter((item) => !item.hideOnDesktop)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [localeOpen, setLocaleOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  // Transparent (hero-overlay) nav rides UP with the scroll like a sticky nav
  // instead of jumping. `navTop` is the live top offset in px: it tracks the
  // banner bottom (36 → 0) as you scroll the first 36px, then pins at 0 — the
  // exact motion a `sticky top-0` header produces on default-nav pages. Null
  // until the client effect measures (SSR renders at the at-top resting value).
  const [navTop, setNavTop] = useState<number | null>(null)
  const [videoSrc, setVideoSrc] = useState<string | null>(null)
  // Stamped onto the <header> after the first client commit, i.e. once the nav
  // links are real next/link handlers rather than the plain <a href>s the
  // server sent. A click that lands before that is a FULL document navigation,
  // which is a visitor's first tap reloading the site — and in Playwright it
  // silently turned e2e/static-shell.spec.ts's soft-nav proof into a hard
  // reload, where every assertion still passed. Reproduced at 8x CPU throttle:
  // 3 of 4 clicks issued a document request. Same `data-hydrated` convention
  // as the calculator widgets under app/.../tools/.
  const [hydrated, setHydrated] = useState(false)
  const locale = useLocale()
  const localePath = useLocalePath()
  const t = useTranslations("nav")
  const router = useRouter()
  // `usePathname()` drives SPA-nav-only side effects (close menus on
  // navigation, locale-switch URL rewriting). Transparent mode is NOT
  // read from here — it's a prop passed in by the route group's layout
  // at SSR time, so the server HTML already has the correct transparent
  // state and there's no hydration flicker on hero-overlay pages.
  const pathname = usePathname()

  useEffect(() => setHydrated(true), [])

  // Close all menus on navigation
  useEffect(() => {
    setOpenDropdown(null)
    setMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!transparent) return
    // The at-top resting offset: just below the 36px banner (or 0 if hidden).
    const base = bannerVisible ? 36 : 0
    let raf = 0
    const compute = () => {
      const y = window.scrollY
      setScrolled(y > 0)
      // Ride the nav up 1:1 with scroll until it reaches the top, then pin.
      // Mirrors a `sticky top-0` header: no instant jump, backing fades in via
      // the pill's own background transition while it travels.
      setNavTop(Math.max(0, base - y))
    }
    const handler = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(compute)
    }
    compute() // check initial scroll position
    window.addEventListener("scroll", handler, { passive: true })
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("scroll", handler)
    }
  }, [transparent, bannerVisible])

  function switchLocale(newCountry: string, newLanguage: string) {
    const segments = window.location.pathname.split("/").filter(Boolean)
    const pagePath = segments.slice(2).join("/")
    const currentPath = pagePath ? `/${pagePath}` : ""
    const newPath = buildLocaleSwitchUrl({
      currentPath,
      targetCountry: newCountry as Country,
      targetLocale: newLanguage,
    })
    // Preserve scroll position when switching locales — users expect to stay
    // in place, not jump to the top (AHD-5582).
    router.push(newPath, { scroll: false })
    setLocaleOpen(false)
    document.cookie = `preferred-language=${newLanguage};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
  }

  const hasMegaMenuOpen = items.some(
    (item) => item.megaMenu && openDropdown === item.href
  )
  // Visual style: transparent only when not scrolled, no dropdown, no mobile menu
  const isTransparent = transparent && !scrolled && !openDropdown && !mobileOpen
  return (
    <>
      <header
        data-hydrated={hydrated ? "true" : undefined}
        className={cn(
          // Default-nav pages: a plain `sticky top-0` header rides up with the
          // page and pins at the top — the browser handles the motion natively.
          // Hero-overlay (transparent) pages must sit OVER the hero, so they're
          // `fixed`; the sticky-like ride-up is reproduced by `navTop` (the
          // inline `top` below) tracking the scroll instead of jumping.
          transparent
            ? "fixed z-50 w-full"
            : "sticky top-0 z-50 h-[72px] w-full",
          className
        )}
        // The 8px breathing gap under the banner (and from the viewport when
        // scrolled) is applied as `md:pt-2` on the inner wrapper below — NOT via
        // `top` — so the gap is identical at rest and when scrolled (AHD nav-gap).
        style={
          transparent ? { top: navTop ?? (bannerVisible ? 36 : 0) } : undefined
        }
      >
        <div
          className={cn("mx-auto max-w-[1440px] px-0 md:px-4", "pt-0 md:pt-2")}
        >
          <div
            className={cn(
              "relative z-50 rounded-none transition-[background-color,backdrop-filter] duration-300 will-change-[backdrop-filter] md:rounded-[32px]",
              isTransparent
                ? "bg-transparent"
                : hasMegaMenuOpen
                  ? "bg-white/80 backdrop-blur-xl"
                  : // iOS Safari bands at very small blur radii over sharp imagery —
                    // a 12px (md) radius looks clean and matches the mega-menu state.
                    "bg-white/70 backdrop-blur-md"
            )}
            style={{ isolation: "isolate" }}
            onMouseLeave={() => {
              if (hasMegaMenuOpen) setOpenDropdown(null)
            }}
          >
            <div className="flex h-16 items-center pr-2 pl-4 will-change-transform min-[1200px]:pr-[10px] md:pr-3 md:pl-6">
              {/* Logo */}
              <div
                className={cn(
                  // translate-y-[1px]: optical nudge — the logo sat 1px high
                  // against the nav links / controls.
                  "mr-auto flex-shrink-0 translate-y-[1px] transition-colors duration-300",
                  isTransparent ? "text-white" : "text-neutral-900"
                )}
              >
                {logo ? (
                  <Link href={localePath("/")}>{logo}</Link>
                ) : (
                  <Link
                    href={localePath("/")}
                    className={cn(
                      "font-medium",
                      isTransparent ? "text-white" : "text-neutral-900"
                    )}
                  >
                    Ahead
                  </Link>
                )}
              </div>

              {/* Desktop nav */}
              <nav
                className="hidden flex-1 items-center justify-center gap-0.5 min-[1200px]:flex"
                aria-label="Main navigation"
              >
                {desktopItems.map((item) => (
                  <div
                    key={item.href}
                    className="relative"
                    onMouseEnter={() =>
                      setOpenDropdown(item.children ? item.href : null)
                    }
                    onMouseLeave={() => {
                      if (!item.megaMenu) setOpenDropdown(null)
                    }}
                  >
                    {item.children ? (
                      <button
                        type="button"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === item.href ? null : item.href
                          )
                        }
                        className={cn(
                          "inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-full pr-3 pl-4 text-[16px] leading-[16px] font-normal tracking-[0px] whitespace-nowrap transition-colors", // ds-typo-ignore — navbar link size per Figma spec
                          isTransparent
                            ? "text-white/90 hover:bg-white/10 hover:text-white"
                            : "text-neutral-900 hover:bg-white"
                        )}
                      >
                        {item.label}
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          aria-hidden="true"
                          className={cn(
                            "transition-transform",
                            openDropdown === item.href && "rotate-180"
                          )}
                        >
                          <path
                            d="M3 4.5L6 7.5L9 4.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    ) : (
                      <Link
                        href={item.href}
                        prefetch
                        className={cn(
                          "inline-flex h-10 items-center gap-1 rounded-full px-4 text-[16px] leading-[16px] font-normal tracking-[0px] whitespace-nowrap transition-colors", // ds-typo-ignore — navbar link size per Figma spec
                          isTransparent
                            ? "text-white/90 hover:bg-white/10 hover:text-white"
                            : "text-neutral-900 hover:bg-white"
                        )}
                      >
                        {item.label}
                      </Link>
                    )}
                    {/* Regular dropdown (non-mega, only when no mega menu is open) */}
                    {item.children &&
                      !item.megaMenu &&
                      !hasMegaMenuOpen &&
                      openDropdown === item.href && (
                        <DropdownMenu items={item.children} />
                      )}
                  </div>
                ))}
              </nav>

              {/* Right side: locale picker + Login + CTA */}
              <div className="flex flex-shrink-0 items-center gap-2 md:gap-2">
                {/* Locale picker + search + Login — tighter group. min-width
                 *  holds the cluster steady so it never reflows. Gated to md+:
                 *  below it every child is desktop-only (hidden md:…), so on
                 *  mobile the reserved 112px is empty and would push the
                 *  hamburger off-screen. */}
                <div className="flex items-center justify-end gap-0.5 md:min-w-[112px]">
                  <DesktopLocalePicker
                    localeOpen={localeOpen}
                    setLocaleOpen={setLocaleOpen}
                    switchLocale={switchLocale}
                    isTransparent={!!isTransparent}
                  />

                  {/* Desktop search icon — CH only (DE/NL keep the slim nav). */}
                  <CountryGate id="feature:nav-search">
                    <SearchTrigger
                      surface="navbar-desktop"
                      transparent={!!isTransparent}
                    />
                  </CountryGate>

                  {/* Login — a plain icon-in-a-circle, styled identically to the
                   *  search trigger (hover = white circle, no expanding label,
                   *  no shadow). Gated to CH only — DE/NL have no local login. */}
                  <CountryGate id="feature:nav-login">
                    <span className="relative hidden h-10 w-10 flex-shrink-0 md:inline-flex">
                      <a
                        href={`https://my.aheadhealth.com/${locale}/sign_in`}
                        aria-label={t("login")}
                        title={t("login")}
                        className={cn(
                          "flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200",
                          isTransparent
                            ? "text-white/90 hover:bg-white/10 hover:text-white"
                            : "text-neutral-900 hover:bg-white"
                        )}
                      >
                        <UserCircleIcon width={20} height={20} />
                      </a>
                    </span>
                  </CountryGate>
                </div>
                {ctaLabel &&
                  (onCtaClick || ctaHref) &&
                  (onCtaClick ? (
                    // `Button` already renders a <button>, so wrapping it in
                    // another one produced nested interactive buttons. That is
                    // invalid HTML: the parser auto-closes the outer <button>,
                    // so the browser's DOM has them as SIBLINGS while React
                    // expects them nested — a guaranteed hydration mismatch on
                    // every waitlist-market page (NL, DE), plus a stray empty
                    // focusable button in the tab order. Sentry
                    // MARKETING-SITE-N. The wrapper's onClick was a duplicate
                    // of the one below, so dropping it changes no behaviour.
                    <Button
                      variant={isTransparent ? "outline" : undefined}
                      className={
                        isTransparent
                          ? "max-md:type-body-md max-[360px]:type-body-sm border-white bg-transparent text-white hover:bg-white/10 max-[360px]:px-3 max-md:h-10 max-md:border-white/50 max-md:px-[18px] max-md:py-[8px] max-md:font-[520] min-[1200px]:px-6"
                          : "bg-aubergine-800 hover:bg-aubergine-900 min-[360px]:max-md:type-body-md max-[360px]:type-body-sm border border-transparent max-[360px]:px-3 max-md:h-10 max-md:py-[8px] max-md:font-[520] min-[360px]:max-md:px-[18px] min-[1200px]:px-6"
                      }
                      onClick={() => {
                        onCtaClick()
                        track.ctaClick(ctaLabel, "navbar", "waitlist")
                      }}
                    >
                      {ctaLabel}
                    </Button>
                  ) : (
                    <a
                      href={ctaHref}
                      onClick={() =>
                        track.ctaClick(ctaLabel, "navbar", ctaHref!)
                      }
                    >
                      {isTransparent ? (
                        <Button
                          variant="outline"
                          className="max-md:type-body-md max-[360px]:type-body-sm border-white bg-transparent text-white hover:bg-white/10 max-[360px]:px-3 max-md:h-10 max-md:border-white/50 max-md:px-[18px] max-md:py-[8px] max-md:font-[520] min-[1200px]:px-6"
                        >
                          {ctaLabel}
                        </Button>
                      ) : (
                        <Button className="bg-aubergine-800 hover:bg-aubergine-900 min-[360px]:max-md:type-body-md max-[360px]:type-body-sm border border-transparent max-[360px]:px-3 max-md:h-10 max-md:py-[8px] max-md:font-[520] min-[360px]:max-md:px-[18px] min-[1200px]:px-6">
                          {ctaLabel}
                        </Button>
                      )}
                    </a>
                  ))}
                <button
                  type="button"
                  className={cn(
                    "p-2 transition-colors min-[1200px]:hidden",
                    isTransparent
                      ? "text-white hover:text-white/80"
                      : "text-neutral-600 hover:text-neutral-900"
                  )}
                  aria-label={mobileOpen ? "Close menu" : "Open menu"}
                  onPointerDown={preloadMobileMenu}
                  onFocus={preloadMobileMenu}
                  onClick={() => {
                    setMobileOpen((v) => !v)
                    setOpenDropdown(null)
                  }}
                >
                  {mobileOpen ? (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M15 5L5 15M5 5l10 10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 6h14M3 10h14M3 14h14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Mega menu — animated expansion inside the pill */}
            {/* safe-animate-presence: conditional-mount — gated by `hasMegaMenuOpen=true` */}
            <AnimatePresence>
              {hasMegaMenuOpen &&
                (() => {
                  const megaItem = items.find(
                    (item) => item.megaMenu && openDropdown === item.href
                  )
                  if (!megaItem?.children) return null
                  return (
                    <m.div
                      key={megaItem.megaMenuType || megaItem.href}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        height: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                        opacity: { duration: 0.25, ease: "easeOut" },
                      }}
                      className="hidden overflow-hidden min-[1200px]:block"
                      onMouseEnter={() => setOpenDropdown(megaItem.href)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {megaItem.megaMenuType === "whatWeOffer" ? (
                        <WhatWeOfferMegaMenu navHiddenPaths={navHiddenPaths} />
                      ) : (
                        <HowItWorksMegaMenu
                          megaItem={megaItem}
                          onPlayVideo={(src) => {
                            setOpenDropdown(null)
                            setVideoSrc(src)
                          }}
                        />
                      )}
                    </m.div>
                  )
                })()}
            </AnimatePresence>
          </div>
        </div>

        {/* Sidebar menu — slides in from right */}
        {/* safe-animate-presence: conditional-mount — gated by `mobileOpen=true` */}
        <AnimatePresence>
          {mobileOpen && (
            <MobileMenu
              items={items}
              ctaLabel={ctaLabel}
              ctaHref={ctaHref}
              onCtaClick={onCtaClick}
              navHiddenPaths={navHiddenPaths}
              setMobileOpen={setMobileOpen}
              switchLocale={switchLocale}
            />
          )}
        </AnimatePresence>
      </header>

      {/* Warms the browser cache for mega-menu images after idle */}
      <MegaMenuPreloader />

      {/* Video overlay modal */}
      {/* safe-animate-presence: conditional-mount — gated by `videoSrc` truthy */}
      <AnimatePresence>
        {videoSrc && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            role="dialog"
            aria-modal="true"
            aria-label="Video player"
            className="fixed inset-0 z-[200] flex items-center justify-center overscroll-contain bg-black/80 backdrop-blur-sm"
            onClick={() => setVideoSrc(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setVideoSrc(null)
            }}
          >
            <m.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[9/16] w-[90vw] max-w-[480px] overflow-hidden rounded-[20px] bg-black shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={videoSrc}
                autoPlay
                controls
                playsInline
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => setVideoSrc(null)}
                className="absolute top-3 right-3 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
                aria-label="Close video"
                autoFocus
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M1 1l12 12M13 1L1 13" />
                </svg>
              </button>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  )
}
